import { useEffect, useMemo, useRef } from "react";

/**
 * How the form draft validates, in one place rather than in each Formik that
 * renders the draft's fields, so that measuring the form and shipping it cannot
 * disagree about it.
 */

/**
 * How long typing must pause before the form is re-validated. Long enough that
 * a normal typing rhythm produces one validation rather than one per character,
 * short enough that an error the user has already been shown clears about as
 * soon as they stop typing.
 */
const VALIDATION_PAUSE_MS = 300;

/**
 * Formik validates the whole part against the schema on every change by
 * default, which on a long form is the largest single cost of a keystroke and
 * produces a second render of every field to deliver a result no field is
 * showing yet. Errors still surface on blur and on submit, and
 * {@link DebouncedValidation} refreshes any already on screen.
 */
export const formDraftValidationProps = { validateOnChange: false } as const;

/**
 * How many individual fields `touched` marks, counting the leaves rather than
 * the top-level keys: a field addressing part of a value marks
 * `{ hospital: { address: true } }`, so several of them share one key while
 * each becomes displayable in its own right.
 */
function countTouched(touched: unknown): number {
  if (typeof touched === "boolean") return touched ? 1 : 0;
  if (typeof touched !== "object" || touched === null) return 0;
  return Object.values(touched).reduce<number>(
    (total, nested) => total + countTouched(nested),
    0,
  );
}

/**
 * Re-validates the form shortly after typing stops, so an error already on
 * screen updates without waiting for the field to lose focus, and immediately
 * when a field becomes able to display an error, which can happen in the same
 * change that gives it its value.
 *
 * Only once something could be displaying one: before the first blur and before
 * a submit attempt no field shows an error, so validating would be work whose
 * result nobody can see.
 */
export function DebouncedValidation({
  values,
  touched,
  submitCount,
  validateForm,
}: {
  /** Opaque: this only watches it for change, never reads it. */
  values: unknown;
  touched: Record<string, unknown>;
  submitCount: number;
  validateForm: () => Promise<unknown>;
}) {
  // Recomputed only when the answer can change. `touched` keeps its identity
  // while the user types, since typing sets values rather than touching
  // fields, so this stays off the path the rest of this file exists to keep
  // cheap.
  const couldBeShowingAnError = useMemo(
    () => submitCount > 0 || Object.keys(touched).length > 0,
    [touched, submitCount],
  );

  // Read when the timer fires rather than depended on, so that opening the
  // gate does not itself schedule a debounced pass: the blur or submit that
  // opened it has already validated.
  const gate = useRef(couldBeShowingAnError);
  gate.current = couldBeShowingAnError;

  // How many fields could be displaying an error, so that a field which gains
  // that ability can be told apart from one losing it.
  const displayableFields = useMemo(() => countTouched(touched), [touched]);
  const previouslyDisplayable = useRef(displayableFields);

  // Both reasons to validate share one effect, so that whichever applies drops
  // what the last change scheduled: React runs the previous cleanup before this
  // body, so a pass still waiting on the values this one is about to check is
  // already cancelled by the time either branch runs.
  useEffect(() => {
    const gainedOne = displayableFields > previouslyDisplayable.current;
    previouslyDisplayable.current = displayableFields;

    // A field can be marked touched by the same change that first gives it a
    // value, rather than by a blur after it. It then starts displaying errors
    // computed before that change — which describe it as empty — so the form
    // appears to reject the value just supplied. Waiting out the pause would
    // leave that on screen long enough to be believed.
    //
    // Counted rather than watched for identity so that marking a field
    // untouched does not validate: nothing became displayable, and a branch
    // closing does it to every field it owns. One pass per transition into
    // being displayable, which a field behind a reopened branch makes more than
    // once; where that transition is a blur rather than a fill it repeats the
    // pass the blur already ran, the price of not being able to tell the two
    // apart.
    if (gainedOne) {
      void validateForm();
      return undefined;
    }

    const timer = setTimeout(() => {
      if (gate.current) void validateForm();
    }, VALIDATION_PAUSE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [values, displayableFields, validateForm]);

  return null;
}
